/**
 * Marketing Section — standardized container with max-width 1280 + responsive padding.
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-010
 */

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import {
  VideoBackground,
  type VideoBackgroundProps,
} from '@/components/marketing/ui/video-background';

export interface SectionProps extends HTMLAttributes<HTMLElement> {
  /** Apply default vertical padding (py-16 lg:py-24). Default true. */
  padded?: boolean;
  /** Remove horizontal max-width (full-bleed background). Default false. */
  bleed?: boolean;
  /**
   * Optional ambient video background. When set, adds `isolate overflow-hidden`,
   * renders `<VideoBackground>` behind, and wraps children in a `relative` div
   * so they paint above the video.
   */
  videoBackground?: Pick<
    VideoBackgroundProps,
    'srcWebm' | 'srcMp4' | 'poster' | 'overlayOpacity' | 'loop'
  >;
  children?: ReactNode;
}

export function Section({
  id,
  padded = true,
  bleed = false,
  videoBackground,
  className,
  children,
  ...rest
}: SectionProps) {
  return (
    <section
      id={id}
      className={cn(
        'relative',
        videoBackground && 'isolate overflow-hidden',
        bleed ? 'w-full' : 'mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8',
        padded && 'py-16 lg:py-24',
        className
      )}
      {...rest}
    >
      {videoBackground ? (
        <>
          <VideoBackground {...videoBackground} />
          <div className="relative">{children}</div>
        </>
      ) : (
        children
      )}
    </section>
  );
}
